import { useEffect, useState, useMemo } from 'react';
import { FormGroup, FormLabel } from 'react-bootstrap';
import { useCartContext } from '../../../context/CartContext/CartContext';
import ShopSideBar from '../../components/SideBar/ShopSideBar';
import style from './home.module.scss';
import ProductCard from '../../components/ProductCard/ProductCard';
import cx from 'classnames';
import {
  MdViewList as ListIcon,
  MdViewModule as GridIcon
} from 'react-icons/md';
import { Product } from '../../../models/Product/Product';
import { useQuery } from '@tanstack/react-query';
import { collect, Collection } from 'collect.js';
import { ProductAttribute } from '../../../models/Product/contracts';
import axiosAPI from '../../../utils/client/axiosAPI';
import { Select, TreeSelect } from 'antd';
import ShopPagination from '../../components/Pagination/ShopPagination';
import { Category } from '../../../models/Category/Category';
import { Media } from '../../../models/Media/Media';
import CookiesConsent from '../../../components/CookiesConsent/CookiesConsent';
import { toast } from 'react-toastify';
import { ProductStatus } from '../../../models/Product/contracts';

function Home(props: any) {
  const { cart } = useCartContext();
  const { collapsedSidebar, setCollapsedSidebar } = props;

  // --- REFACTORED: Centralized all filtering state into one object ---
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 40,
    categories: [] as number[],
    otherFilters: {} as Record<number, number[]>
  });

  const [shopListView, setShopListView] = useState<boolean>(false);

  // --- REFACTORED: Simplified fetching for general filters ---
  const { data: generalFilters = [] } = useQuery(['filters'], async () => {
    const { data } = await axiosAPI.get('/product/filters');
    return data || [];
  });

  // --- REFACTORED: Simplified query key and parameter passing ---
  const { data: productsResponse, isLoading } = useQuery(
    ['products', filters], // The query now simply depends on the single filters state object
    () =>
      Product.all({
        page: filters.page,
        page_size: filters.pageSize,
        categories: filters.categories.join(','),
        filters: Object.values(filters.otherFilters).flat().join(',')
      }),
    {
      keepPreviousData: true
    }
  );

  const { data: categories } = useQuery(['filter-categories'], () =>
    Category.all()
  );

  return (
    <div className={style.pageContainer}>
      <ShopSideBar
        collapsed={collapsedSidebar}
        setCollapsed={setCollapsedSidebar}
        heading="Filteri"
      >
        <>
          <FormGroup className="cs-filter-group">
            <FormLabel>Kategorija</FormLabel>
            <TreeSelect
              dropdownStyle={{ maxHeight: 500, overflow: 'auto' }}
              showSearch
              treeNodeFilterProp="title"
              treeDefaultExpandAll
              placeholder="Izaberi kategoriju"
              treeData={categories ?? []}
              multiple
              value={filters.categories}
              onChange={(value) => {
                setFilters((prev) => ({ ...prev, categories: value, page: 1 }));
              }}
              className="cs-tree-select"
            />
          </FormGroup>
          {Array.isArray(generalFilters) &&
            generalFilters.map(({ id, name, options }: any) => {
              return (
                <FormGroup className="cs-filter-group" key={id}>
                  <FormLabel>{name}</FormLabel>
                  <Select
                    placeholder={`Select ${name}`}
                    mode="multiple"
                    value={filters.otherFilters[id] || []}
                    onChange={(values) => {
                      setFilters((prev) => ({
                        ...prev,
                        otherFilters: { ...prev.otherFilters, [id]: values },
                        page: 1
                      }));
                    }}
                  >
                    {Array.isArray(options) &&
                      options.map(({ id, value }: any) => {
                        return <Select.Option key={id}>{value}</Select.Option>;
                      })}
                  </Select>
                </FormGroup>
              );
            })}
        </>
      </ShopSideBar>

      <div
        className={cx(
          style.shopPageWrapper,
          shopListView ? style.list : null,
          collapsedSidebar ? style.collapsed : null,
          'cs-content'
        )}
      >
        <div className={style.pageHeaderContainer}>
          <h1>Katalog kostima</h1>
          <p>
            Nudimo vam najam kostima i rekvizita iz bogate kolekcije Fundusa
            Avala studios d.o.o. Beograd. Ponuda obuhvata različite epohe koje
            možete pronaći u našem katalogu na ovom sajtu. Izborom odgovarajućih
            proizvoda iz naše ponude, u mogućnosti ste da izvršite rezervaciju
            željenih artikala koje želite da iznajmite.
          </p>
        </div>
        <div className={style.topBar}>
          <div className={style.left}>
            <div className={style.infoContainer}>
              Prikazano{' '}
              <span>
                {productsResponse?.pagination.count ?? 0}/
                {productsResponse?.pagination.total ?? 0}
              </span>{' '}
              proizvoda
            </div>
            <div className={style.selectContainer}>
              <Select
                value={filters.pageSize.toString()}
                className="cs-select"
                onChange={(val) => {
                  setFilters((prev) => ({
                    ...prev,
                    pageSize: parseInt(val),
                    page: 1
                  }));
                }}
                options={[
                  { value: 20, label: '20' },
                  { value: 40, label: '40' },
                  { value: 80, label: '80' },
                  { value: 160, label: '160' },
                  { value: 320, label: '320' }
                ]}
              />
            </div>
          </div>
          <div className={cx(style.right, shopListView ? style.active : null)}>
            <ListIcon
              className={shopListView ? style.active : ''}
              onClick={() => setShopListView(true)}
            />
            <GridIcon
              className={!shopListView ? style.active : ''}
              onClick={() => setShopListView(false)}
            />
          </div>
        </div>
        <div className={style.content}>
          {isLoading && <p>Učitavanje proizvoda...</p>}
          {!isLoading &&
            productsResponse?.data.map((product) => {
              const image = product.getMainImageUrl();

              return (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  image={image ?? ''}
                  name={product.name}
                  price={product.price * (cart?.priceFactor ?? 1)}
                  isAvailable={product.status === ProductStatus.AVAILABLE}
                  url={`/product/${product.id}`}
                  attributes={product.attributes} 
                  listView={shopListView}
                />
              );
            })}
        </div>
        <div className={style.pagination}>
          {!!productsResponse?.pagination && (
            <ShopPagination
              pagination={productsResponse?.pagination}
              onChange={(newPage) => {
                setFilters((prev) => ({ ...prev, page: newPage }));
              }}
            />
          )}
        </div>
        <CookiesConsent />
      </div>
    </div>
  );
}

export default Home;
